import React, { useEffect, useState } from 'react';
import { Cards } from '../shared/Cards';
import { paginateArray } from '../../helpers/utils'; // Importa la función de paginado
import { Publish } from '../shared/Publish';
import { useLocation } from 'react-router-dom';
import FiestaSpinner from '../shared/FiestaSpinner';
import { Contactanos } from '../shared/Contactanos';
import { API_BASE_URL } from '../../config/config.js'; 

// Mapeo de tipos de lugar a sus IDs
const typeToIdMap = {
  jardin: 1,
  infantil: 2,
  salon: 3,
  hacienda: 4,
  terraza: 5,
};

// Configuración para la paginación
const pageSizeFirstSection = 6;  // Número de elementos a mostrar en la primera sección
const pageSizeSecondSection = 12; // Número de elementos a mostrar en la segunda sección
const totalPageSize = pageSizeFirstSection + pageSizeSecondSection; 
const maxVisiblePages = 6;  // Número máximo de páginas visibles en el paginador

export const EventosPrincipal = ({ eventType }) => {
  const location = useLocation();  // Obtiene la ubicación actual, útil para el manejo del scroll
  const [currentPage, setCurrentPage] = useState(0); // Página actual de los eventos
  const [pageWindowStart, setPageWindowStart] = useState(0); // Controla la ventana de páginas visibles en el paginador
  const [services, setServices] = useState([]);  // Almacena los datos de los eventos
  const [loading, setLoading] = useState(true);  // Estado para verificar si los datos están cargando
  const [error, setError] = useState(null);  // Estado para almacenar errores, si los hay
  const [totalPages, setTotalPages] = useState(0);  // Estado para total de páginas

  // Obtener el ID del tipo de lugar según el tipo de evento
  const typeId = typeToIdMap[eventType] || 1;  // Si el tipo de evento no se encuentra, se utiliza un valor por defecto

  useEffect(() => {
    // Verifica si el estado de la ubicación contiene la clave 'scrollToTop' y desplaza la página hacia arriba
    if (location.state?.scrollToTop) {
      window.scrollTo(0, 0);
    }
    
    const page = 0;
    const size = 100;
    const sortBy = 'id';
  
    // Construir la URL dinámica
    const url = `${API_BASE_URL}/lugares/tipoLugares/${typeId}?page=${page}&size=${size}&sortBy=${sortBy}`;
  

    // Llamada a la API para obtener los eventos según el tipo
    fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`Error HTTP: ${response.status}`);
      }
      return response.json();
    })
    .then(data => {
      setServices(data.content);  // Almacena los datos obtenidos en el estado
      setLoading(false);  // Marca que la carga ha finalizado
      const totalElements = data.totalElements;
      const calculatedTotalPages = Math.ceil(totalElements / totalPageSize); // Calcula las páginas totales
      setTotalPages(calculatedTotalPages);  // Actualiza el estado de totalPages
      console.log('Datos recibidos:', data);
      console.log('Total de elementos:', totalElements);
      console.log('Total de páginas:', calculatedTotalPages);
    })
    .catch(error => {
      setError(error.message);  // Si ocurre un error, lo almacenamos
      setLoading(false);  // Marca que la carga ha finalizado, aunque haya ocurrido un error
    });

  }, [eventType, location]);  // Este efecto se ejecuta cuando cambia el tipo de evento o la ubicación

  // Si está cargando, muestra un mensaje de carga
  if (loading) return <div className="centered-spinner">
      <FiestaSpinner/>;
  </div>
  
  // Si ocurre un error, muestra el mensaje de error
  if (error) return <div>Error: {error}</div>;

  // Cálculos de paginación
  const totalItems = services.length;  // Número total de elementos

  // Función para obtener los elementos de la primera sección
  const getFirstSectionItems = () => {
    const startIndex = currentPage * totalPageSize;  // Calcula el índice de inicio según la página actual
    return services.slice(startIndex, startIndex + pageSizeFirstSection);  // Retorna los elementos correspondientes a la primera sección
  };

  // Función para obtener los elementos de la segunda sección
  const getSecondSectionItems = () => {
    const startIndex = currentPage * totalPageSize;  // Calcula el índice de inicio para la segunda sección
    const remainingItems = services.slice(startIndex + pageSizeFirstSection);  // Obtiene los elementos restantes
    const paginatedItems = paginateArray(remainingItems, pageSizeSecondSection);  // Aplica el paginado a los elementos restantes
    return paginatedItems[0] || [];  // Retorna los elementos de la segunda sección
  };

  // Obtiene los elementos de la primera y segunda sección
  const firstSectionItems = getFirstSectionItems();
  const secondSectionItems = getSecondSectionItems();

  // Función para cambiar la página
  const handlePageChange = (page) => {
    setCurrentPage(page);  // Establece la página actual
    // Actualiza la ventana de páginas visibles si es necesario
    if (page < pageWindowStart || page >= pageWindowStart + maxVisiblePages) {
      setPageWindowStart(Math.max(0, page - Math.floor(maxVisiblePages / 2)));
    }
    window.scrollTo(0, 0);  // Desplaza la página hacia arriba
  };

  // Genera los botones de la paginación
  const generatePageButtons = () => {
    const buttons = [];
    const start = pageWindowStart;
    const end = Math.min(pageWindowStart + maxVisiblePages, totalPages);  // Establece el rango de páginas a mostrar
    for (let i = start; i < end; i++) {
      buttons.push(
        <button
          key={i}
          className={`page-button ${i === currentPage ? 'active' : ''}`}  // Marca la página actual
          onClick={() => handlePageChange(i)}  // Llama a la función de cambio de página
        >
          {i + 1}
        </button>
      );
    }
    return buttons;
  };

  // Función para ir a la siguiente página
  const nextPage = () => {
    setCurrentPage(prevPage => {
      const newPage = Math.min(prevPage + 1, totalPages - 1);  // Calcula la siguiente página
      handlePageChange(newPage);  // Cambia a la siguiente página
      return newPage;
    });
  };

  // Función para ir a la página anterior
  const prevPage = () => {
    setCurrentPage(prevPage => {
      const newPage = Math.max(prevPage - 1, 0);  // Calcula la página anterior
      handlePageChange(newPage);  // Cambia a la página anterior
      return newPage;
    });
  };

  return (
    <div className='events-detail'>
      {/* Muestra los controles de paginación solo si hay más de una página */}
      {totalPages > 1 && (
        <div className="pagination-controls">
          <button
            className="pagination-button"
            onClick={prevPage}  // Al hacer clic, va a la página anterior
            disabled={currentPage === 0}  // Deshabilita el botón si ya estamos en la primera página
          >
            Anterior
          </button>
          {generatePageButtons()}  {/* Genera los botones de la paginación */}
          <button
            className="pagination-button"
            onClick={nextPage}  // Al hacer clic, va a la siguiente página
            disabled={currentPage === totalPages - 1}  // Deshabilita el botón si ya estamos en la última página
          >
            Siguiente
          </button>
        </div>
      )}

      {/* Muestra un mensaje si no hay elementos */}
      {totalItems === 0 ? (
        <div className="no-more-items">
        <p>No contamos por el momento con espacios para tu evento.</p>
        <Contactanos />
        </div>
        
      ) : (
        <>
          {/* Muestra la primera sección de eventos */}
          {firstSectionItems.length > 0 && <Cards items={firstSectionItems} itemType={eventType} />}
          
          {/* Espacio publicitario */}
          <Contactanos />
          {/* <Publish imageKey="publicidad1" /> */}

          {/* Muestra la segunda sección de eventos */}
          {secondSectionItems.length > 0 && <Cards items={secondSectionItems} itemType={eventType} />}
        </>
      )}

        {/* Muestra los controles de paginación solo si hay más de una página */}
        {totalPages > 1 && (
        <div className="pagination-controls">
          <button
            className="pagination-button"
            onClick={prevPage}  // Al hacer clic, va a la página anterior
            disabled={currentPage === 0}  // Deshabilita el botón si ya estamos en la primera página
          >
            Anterior
          </button>
          {generatePageButtons()}  {/* Genera los botones de la paginación */}
          <button
            className="pagination-button"
            onClick={nextPage}  // Al hacer clic, va a la siguiente página
            disabled={currentPage === totalPages - 1}  // Deshabilita el botón si ya estamos en la última página
          >
            Siguiente
          </button>
        </div>
      )}

      {console.log('Primeros elementos:', firstSectionItems)}
    {console.log('Segundos elementos:', secondSectionItems)}
    </div>
  );
};
